import React, { useState, useEffect, useContext } from 'react'
import './CollectTab.scss'

import ThemeSwitcher from 'react-css-vars'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import { Button, Grid } from 'semantic-ui-react'

import { IProject, ITheme, ProjectQuestionResponse } from '../../../api/Data'
import Data from '../../../api/Data'
import { projectImageSize } from '../../../config/constants'
import { GlobalContext } from '../../../contexts/GlobalContext'
import { UserContext } from '../../../contexts/UserContext'

import SubSection from '../../../components/common/SubSection'
import Title from '../../../components/common/Title'
import ImageUploader from '../../../components/modals/ImageUploader'
import UploaderModal from '../../../components/modals/UploaderModal'
import { CollectTab as CollectTabsEnum, isValidCollectTab } from '../../../components/navigation/SideBarSecondary.types'

import CollectTabCollectPage from './CollectTabCollectPage'
import CollectTabCollectPagePreview from './CollectTabCollectPagePreview'
import CollectTabLandingPage from './CollectTabLandingPage'
import CollectTabLandingPagePreview from './CollectTabLandingPagePreview'
import CollectTabQuestionsPage from './CollectTabQuestionsPage'
import CollectTabSuccessPage from './CollectTabSuccessPage'
import CollectTabSuccessPagePreview from './CollectTabSuccessPagePreview'
import CollectTabQualifyingQuestionsPage from './CollectTabQualifyingQuestionsPage'

let autoFetchProject: any

const CollectTab = (props: IProps) => {
  const { hasUserMadeChange, dispatchGlobalContext } = useContext(GlobalContext)
  const { userProfile } = useContext(UserContext)

  // Props
  const { projectTitle, history, location, match, project, theme, setTheme, setProject, exitProject } = props

  const { hasBrandedUploadPage } = userProfile.plan

  // States
  const [activeTab, setActiveTab] = useState<CollectTabsEnum>('landingPage')
  const [validationError, setValidationError] = useState<string | null>(null)
  const [heroVideoEncodingStatus, setHeroVideoEncodingStatus] = useState(project.heroVideoEncodingStatus)
  const [showProjectPictureModal, setShowProjectPictureModal] = useState<boolean>(false)
  const [showProjectVideoModal, setShowProjectVideoModal] = useState(false)
  const [projectQuestions, setProjectQuestions] = useState<ProjectQuestionResponse[]>([])

  const fetchProjectQuestions = async (): Promise<{
    questions: ProjectQuestionResponse[]
    error?: string | undefined
  }> => {
    try {
      if (!project || !project.id) {

        return { questions: [], error: 'Project ID not found' }
      }

      const { data, error } = await Data.getProjectQuestions(project.id, true)

      if (error) {
        console.error('Error fetching questions:', error)
        return { questions: [], error: 'An error occurred. Please try again later.' }
      }

      const questionData = Array.isArray(data) ? data : []
      return { questions: questionData }
    } catch (err) {
      console.error('Error fetching questions:', err)
      return { questions: [], error: 'Failed to load questions' }
    }
  }

  // Url search query navigation
  useEffect(() => {
    const regexMatch = new RegExp(/tab=([A-Za-z]+)/).exec(location.search)
    const targetTab = regexMatch ? regexMatch[1] : undefined

    if (isValidCollectTab(targetTab))
      setActiveTab(targetTab)
    else
      history.push(`/projects/${match.params.projectSlug}/collect?tab=landingPage`)
  }, [history, location.search, match.params.projectSlug])

  // Fetch project if the hero video is encoding
  useEffect(() => {
    const fetchProject = async () => {
      const slug = match.params.projectSlug

      clearTimeout(autoFetchProject)

      const { data: projectData, error } = await Data.getProject(slug)

      if (error) return alert('An error occurred')

      setHeroVideoEncodingStatus(projectData.heroVideoEncodingStatus)

      const videoHeroInProgress = ![0, 199, 200].includes(projectData.heroVideoEncodingStatus)

      // If clips are encoding, fetch clips every 5 seconds
      if (videoHeroInProgress) autoFetchProject = setTimeout(() => fetchProject(), 5000)
    }

    fetchProject()

    return () => clearTimeout(autoFetchProject)
  }, [match.params.projectSlug, setProject])

  useEffect(() => {
    setProject({ ...project, heroVideoEncodingStatus: heroVideoEncodingStatus })
  }, [heroVideoEncodingStatus])

  useEffect(() => {
    const loadProjectQuestions = async () => {
      dispatchGlobalContext({ type: 'set loading', payload: true })

      const { questions, error } = await fetchProjectQuestions()

      dispatchGlobalContext({ type: 'set loading', payload: false })

      if (error) {
        return alert(error)
      }

      setProjectQuestions(questions)
    }

    if (activeTab === 'dataCollection') loadProjectQuestions()
  }, [activeTab])
  
  // ------------------------------------------------Project handles------------------------------------------------
  const handleSaveNewProjectPicture = async (imageBlob: Blob) => {
    const data = new FormData()

    data.append('imageBlob', imageBlob)

    dispatchGlobalContext({ type: 'set loading', payload: true })

    const { error } = await Data.updateProjectImage(project.slug, data)

    if (error) {
      dispatchGlobalContext({ type: 'set loading', payload: false })

      return alert('An error occurred')
    }

    window.location.href = `/projects/${match.params.projectSlug}/collect?tab=landingPage`
  }

  const handleCancelPictureUpload = () => {
    setShowProjectPictureModal(false)
  }

  const handleUploadSuccess = () => {
    setShowProjectVideoModal(false)
    window.location.href = `/projects/${match.params.projectSlug}/collect?tab=landingPage`
  }

  const handleCancelVideoUpload = () => {
    setShowProjectVideoModal(false)
  }

  const handleProjectChange = (e: any, data: any) => {
    const { name, value, checked, values } = data

    // Record any change made on the form
    if (!hasUserMadeChange) dispatchGlobalContext({ type: 'set user has made changes', payload: true })

    // Reset errors
    if (validationError === name) setValidationError(null)
    
    let newValue
    
    if (name === 'showRecentContributions')
      newValue = checked
    // If value is an array
    else if (name === 'questions')
      newValue = values
    // If no deadline, set it to null
    else if (name === 'deadline' && value === '')
      newValue = null
    // Else set new value
    else
      newValue = value
    
    setProject({ ...project, [name]: newValue })
  }
  
  const handleSubmitProject = async () => {
    dispatchGlobalContext({ type: 'set loading', payload: true })

    const { data, error } = await Data.updateProject(project)

    if (error) {
      dispatchGlobalContext({ type: 'set loading', payload: false })
      alert('Oops, something went wrong')
    }

    setTimeout(() => {
      setProject(data)
      dispatchGlobalContext({ type: 'set loading', payload: false })
    }, 1000)
  }

  // ------------------------------------------------Theme handles------------------------------------------------
  const handleSubmitTheme = async () => {
    dispatchGlobalContext({ type: 'set loading', payload: true })

    const { data, error } = await Data.updateTheme(theme)

    dispatchGlobalContext({ type: 'set user has made changes', payload: false })

    if (error) {
      dispatchGlobalContext({ type: 'set loading', payload: false })

      dispatchGlobalContext({ type: 'set user has made changes', payload: false })

      return alert(`An error occurred while updating your theme => ${error.response?.data}`)
    }

    setTheme(data)

    setTimeout(() => dispatchGlobalContext({ type: 'set loading', payload: false }), 1000)
  }
  
  // TODO: here we save both the project and the theme every time the user clicks "save". We should separate that.
  const hasFormError = () => {
    // Use to test if a field is a absolute url
    const UrlRegex = new RegExp(
      /(http|https):\/\/www\.[a-zA-Z]+\.[a-zA-Z]+|www\.[a-zA-Z]+\.[a-zA-Z]+|[a-zA-Z]+\.[a-z]+/,
      'i'
    )

    const { title, description, teamTermsUrl, successTextDescription, successCTAText, successCTALink } = project
    console.log(UrlRegex.test(successCTALink))
    console.log(successCTALink)
    
    if (!title) {
      setValidationError('title')
      history.push(`/projects/${match.params.projectSlug}/collect?tab=landingPage`)
      return true
    }

    if (!description) {
      setValidationError('description')
      history.push(`/projects/${match.params.projectSlug}/collect?tab=landingPage`)
      return true
    }

    if (teamTermsUrl && !UrlRegex.test(teamTermsUrl)) {
      setValidationError('teamTermsUrl')
      history.push(`/projects/${match.params.projectSlug}/collect?tab=landingPage`)
      return true
    }

    if (!successTextDescription) {
      setValidationError('successTextDescription')
      history.push(`/projects/${match.params.projectSlug}/collect?tab=successPage`)
      return true
    }

    if (!successCTAText) {
      setValidationError('successCTAText')
      history.push(`/projects/${match.params.projectSlug}/collect?tab=successPage`)
      return true
    }

    if (!UrlRegex.test(successCTALink)) {
      setValidationError('successCTALink')
      history.push(`/projects/${match.params.projectSlug}/collect?tab=successPage`)
      return true
    }
  }

  const goToNextStep = () => {
    if (location.search.startsWith('?tab=landingPage'))
      history.push(`/projects/${match.params.projectSlug}/collect?tab=questions`)
    if (location.search.startsWith('?tab=questions'))
      history.push(`/projects/${match.params.projectSlug}/collect?tab=dataCollection`)
    if (location.search.startsWith('?tab=dataCollection'))
      history.push(`/projects/${match.params.projectSlug}/collect?tab=successPage`)
    if (location.search.startsWith('?tab=successPage'))
      history.push(`/projects/${match.params.projectSlug}/collaborate`)
  }

  const handleQualifyingQuestions = async (options = { skipRefresh: false }) => {
    // @ts-ignore
    const handlers = window.collectTabQualifyingQuestionsHandlers

    if (!handlers) {
      return activeTab !== 'dataCollection' // missing handlers are fine if not on data collection tab
    }

    if (!handlers.isFormValid()) {
      alert('Some fields need your attention!')
      return false
    }

    if (handlers.hasUnsavedChanges()) {
      const success = await handlers.saveQuestions(options.skipRefresh)

      if (!success) {
        alert('Failed to save questions')
        return false
      }
    }

    return true
  }

  const handleSave = async () => {
    if (hasFormError()) return

    if (!await handleQualifyingQuestions()) return
    handleSubmitProject()
    handleSubmitTheme()
  }

  const handleNext = async () => {
    if (hasFormError()) return

    const canContinue = await handleQualifyingQuestions({ skipRefresh: true })
    if (!canContinue) return

    handleSubmitProject()
    handleSubmitTheme()
    goToNextStep()
  }
  
  const handleVideoHeroClick = () => {
    if (!userProfile!.plan.projectHeroVideo) {
      return dispatchGlobalContext({ type: 'show update plan modal', payload: true })
    }

    return setShowProjectVideoModal(true)
  }

  const { slug, displaySetting } = project
  const { primaryFont, primaryColor, buttonTextColor } = displaySetting

  const uploadPageTheme = {
    primaryFont: hasBrandedUploadPage ? primaryFont : 'Roboto',
    primaryColor: hasBrandedUploadPage ? primaryColor : '#6b50ff',
    buttonTextColor: hasBrandedUploadPage ? buttonTextColor : '#FFFFFF',
  }
  console.log('Rendering Collect tab Container', activeTab)

  return (
    <ThemeSwitcher theme={uploadPageTheme}>
      {/* title */}
      <Title title={projectTitle}>
        <>
          <Button
            compact
            secondary
            disabled={!hasUserMadeChange}
            onClick={handleSave}
            style={{ border: '1px solid #fff' }}
          >
            Save
          </Button>
          {activeTab !== 'successPage' && (
            <Button compact primary onClick={handleNext}>
              Next
            </Button>
          )}
          <Button compact onClick={exitProject}>
            Close
          </Button>
        </>
      </Title>

      <SubSection hasGreyBackground>
        <>
          {activeTab === 'landingPage' && (
            <Grid stackable>
              <Grid.Column width={11}>
                <CollectTabLandingPage
                  project={project}
                  handleChange={handleProjectChange}
                  setShowProjectPictureModal={setShowProjectPictureModal}
                  handleVideoHeroClick={handleVideoHeroClick}
                  validationError={validationError}
                />
              </Grid.Column>

              <Grid.Column width={5}>
                <div className="sticky-preview">
                  <CollectTabLandingPagePreview project={project} handleChange={handleProjectChange} />
                </div>
              </Grid.Column>
            </Grid>
          )}

          {activeTab === 'questions' && (
            <Grid stackable>
              <Grid.Column width={11}>
                <CollectTabQuestionsPage project={project} plan={userProfile.plan} handleChange={handleProjectChange} />
              </Grid.Column>

              <Grid.Column width={5}>
                <div className="sticky-preview">
                  <CollectTabLandingPagePreview project={project} />
                </div>
              </Grid.Column>
            </Grid>
          )}

          {activeTab === 'dataCollection' && (
            <Grid stackable>
              <Grid.Column width={11}>
                <CollectTabCollectPage project={project} handleChange={handleProjectChange} />
                <CollectTabQualifyingQuestionsPage
                  project={project}
                  projectQuestions={projectQuestions}
                  setProjectQuestions={setProjectQuestions}
                  fetchProjectQuestions={fetchProjectQuestions}
                  handleChange={handleProjectChange}
                />
                <Button
                  compact
                  secondary={hasUserMadeChange}
                  inverted={hasUserMadeChange}
                  disabled={!hasUserMadeChange}
                  onClick={handleSave}
                  style={{ border: '1px solid #fff' }}
                  floated="right"
                >
                  Save
                </Button>
              </Grid.Column>

              <Grid.Column width={5}>
                <div className="sticky-preview">
                  <CollectTabCollectPagePreview project={project} projectQuestions={projectQuestions} />
                </div>
              </Grid.Column>
            </Grid>
          )}

          {activeTab === 'successPage' && (
            <Grid stackable>
              <Grid.Column width={11}>
                <CollectTabSuccessPage
                  project={project}
                  handleChange={handleProjectChange}
                  validationError={validationError}
                />
              </Grid.Column>

              <Grid.Column width={5}>
                <div className="sticky-preview">
                  <CollectTabSuccessPagePreview project={project} />
                </div>
              </Grid.Column>
            </Grid>
          )}

          {showProjectVideoModal && (
            <UploaderModal
              title="Upload a video for your landing page"
              endpoint={`/projects/${slug}/updateVideo`}
              requirements={{
                maxFileSize: '160mb',
                maxFiles: 1,
                minDuration: 2,
                acceptedFileTypes: ['mp4', 'mov'],
                acceptedFileMimeTypes: ['video/mp4', 'video/quicktime'],
              }}
              handleUploadSuccess={handleUploadSuccess}
              handleClose={handleCancelVideoUpload}
            />
          )}

          {showProjectPictureModal && (
            <ImageUploader
              inputImageType={projectImageSize.inputImageType}
              outputImageType={projectImageSize.outputImageType}
              imageSize={{ width: projectImageSize.width, height: projectImageSize.height }}
              imageIsAvatar={projectImageSize.imageIsAvatar}
              handleSave={handleSaveNewProjectPicture}
              handleCancel={handleCancelPictureUpload}
            />
          )}
        </>
      </SubSection>
    </ThemeSwitcher>
  )
}

interface IProps extends RouteComponentProps<any> {
  projectTitle: string
  project: IProject
  theme: ITheme
  setProject: (arg: IProject) => void
  setTheme: (arg: ITheme) => void
  exitProject: () => void
}

export default withRouter(CollectTab)
